import {
    type CommentItem,
    CommentSortBy,
    CommentSortDirection,
    GetCommentPageRequest,
    GetCommentsRequest,
} from '@app/_proto/Protos/comments';
import { CommentsClient } from '@app/_proto/Protos/comments.client';
import { StringValue } from '@app/_proto/google/protobuf/wrappers';
import AuthorizationView from '@app/components/auth.view';
import Comment from '@app/components/entity-comments/comment';
import CommentPlaceholder from '@app/components/entity-comments/comment.placeholder';
import CommentSubmit from '@app/components/entity-comments/comment.submit';
import Paging from '@app/components/paging';
import { StyledContentSection } from '@app/components/shared/text-pages.styles';
import { useCommentsUrlHash } from '@app/domains/chapter/hooks/useCommentsUrlHash';
import CommentReport from '@app/domains/review/components/CommentReport';
import { useGrpcApiWithQuery } from '@app/libs/api';
import { useGrpcRequest } from '@app/libs/grpc';
import { useLocalStorage } from '@app/libs/hooks';
import { batch } from '@app/libs/utils';
import { isBrowser } from '@app/utils/utils';
import { FormControl, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import animateScrollTo from 'animated-scroll-to';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

//#region : Global constants and types

/** Custom. */
const CommentSortStorageKey = 'comment-sort';

/** Custom. */
export const SortOptions = [
    {
        label: 'Top',
        value: `${CommentSortBy.Top}:${CommentSortDirection.Desc}`,
    },
    {
        label: 'New',
        value: `${CommentSortBy.Time}:${CommentSortDirection.Desc}`,
    },
    {
        label: 'Old',
        value: `${CommentSortBy.Time}:${CommentSortDirection.Asc}`,
    },
];

/** Custom. */
type SortBy = {
    value: CommentSortBy;
    direction: CommentSortDirection;
};
//#endregion : Global constants and types

//#region : Main component

/** Custom. Main component parameters type */
export type CommentsAreaProps = JSX.IntrinsicElements['div'] & {
    entityId?: number;
    disabled?: boolean;
    disabledMessage?: string;
    paragraphId?: string;
    className?: string;
    onSort?: (sortValue: string) => void;
    onCommentSubmit?: () => void;
};

const PageCount = 15;

/** Custom. Handles comments listing and its actions for a given entity */
function CommentsAreaComponent(props: CommentsAreaProps) {
    //#region : Variables, functions and api calls

    //General
    const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);
    const { entityId, disabled, disabledMessage, className, paragraphId, onSort, onCommentSubmit, ...htmlProps } =
        props;
    const commentsRef = useRef<HTMLDivElement | null>(null); //reference to the comments container
    const [page, setPage] = useState(1); //for pagination
    const [isPaging, setIsPaging] = useState(false);

    const navigate = useNavigate();
    const {
        search: queryString,
        state: historyState,
        hash,
        hashCommentId,
        hashParaId,
    } = useCommentsUrlHash(paragraphId);

    const hashNo = paragraphId === hashParaId ? hashCommentId : undefined;

    const [sort, setSort] = useLocalStorage<SortBy>(
        CommentSortStorageKey,
        {
            value: CommentSortBy.Top,
            direction: CommentSortDirection.Desc,
        },
        {
            sync: false,
        }
    );

    //Comments should be loaded only when the component is in view
    const {
        ref,
        inView,
        entry: parentContainer,
    } = useInView({
        triggerOnce: true,
    });

    const pageRequest = useGrpcRequest(GetCommentPageRequest, {
        id: hashNo!,
        paragraphId: paragraphId ? StringValue.create({ value: paragraphId }) : undefined,
        sortBy: sort?.value,
        sortDirection: sort?.direction,
        count: PageCount,
    });

    const { data: pageData } = useGrpcApiWithQuery(
        CommentsClient,
        c => c.getCommentPage,
        pageRequest,
        ['comment-page', hash, sort?.value, sort?.direction, paragraphId],
        {
            enabled: !!hashNo && !paragraphId,
            staleTime: 0,
            refetchOnWindowFocus: false,
            suspense: true,
        }
    );

    //#region : Server request to fetch comments
    const commentsRequest = useGrpcRequest(GetCommentsRequest, {
        entityId: entityId,
        sortBy: sort?.value,
        sortDirection: sort?.direction,
        paragraphId: paragraphId ? StringValue.create({ value: paragraphId }) : undefined,
        pageInfo: {
            page: page,
            count: PageCount,
        },
    });

    const {
        data: comments,
        refetch: refetchComments,
        ...commentsQuery
    } = useGrpcApiWithQuery(
        CommentsClient,
        c => c.getComments,
        commentsRequest,
        ['comments', entityId, paragraphId, page, sort?.value, sort?.direction],
        {
            cacheTime: 0, //Don't cache
            enabled: !!entityId && (inView || !!pageData),
            keepPreviousData: true,
        }
    );

    //#endregion : Server request to fetch comments

    //#region : Pagination
    const totalPages = useMemo(() => {
        return Math.ceil((comments?.totalTopLevelItems || 0) / PageCount);
    }, [comments?.totalTopLevelItems]);

    const handlePagination = useCallback(async (e, page: number) => {
        batch(() => {
            setPage(page);
            setIsPaging(true);
        });

        if (commentsRef.current) {
            await animateScrollTo(commentsRef.current, {
                maxDuration: 500,
            });
        }
    }, []);

    useEffect(() => {
        setIsPaging(prev => (prev ? false : prev));
    }, [comments]);
    //#endregion : Pagination

    //#region : Sorting
    const handleSort = useCallback(
        (e: SelectChangeEvent) => {
            const sortValue = e.target.value;
            const sort = sortValue.split(':');
            const sortBy: SortBy = {
                value: Number.parseInt(sort[0]) as CommentSortBy,
                direction: Number.parseInt(sort[1]) as CommentSortDirection,
            };

            setSort(sortBy);

            batch(() => {
                setIsPaging(true);
                setPage(1);
            });

            onSort?.(sortValue);
        },
        [setSort, onSort]
    );
    //#endregion : Sorting

    //#region : Submit comment / reply

    const onCommentSubmitted = useCallback(
        async (id: number) => {
            onCommentSubmit?.();
            setSelectedCommentId(id);

            if (
                (sort && (sort.value !== CommentSortBy.Time || sort.direction !== CommentSortDirection.Desc)) ||
                page !== 1
            ) {
                batch(() => {
                    setPage(1);
                    setIsPaging(true);
                    setSort({
                        value: CommentSortBy.Time,
                        direction: CommentSortDirection.Desc,
                    });
                });
            } else {
                refetchComments();
            }
        },
        [page, refetchComments, setSort, sort, onCommentSubmit]
    );

    const onReplySubmitted = useCallback(
        async (id: number) => {
            setSelectedCommentId(id);

            refetchComments();
        },
        [refetchComments]
    );

    useEffect(() => {
        if (pageData) {
            const pageNo = pageData.pageNumber;

            if (pageNo > 0) {
                setPage(pageNo);
            }
        }
    }, [pageData]);

    useEffect(() => {
        if (hashNo) {
            setSelectedCommentId(hashNo);
        }
    }, [hashNo]);

    useEffect(() => {
        if (isBrowser() && selectedCommentId !== null && commentsRef.current) {
            const commentId = `comment-${selectedCommentId}${paragraphId ? `--${paragraphId}` : ''}`;

            const goToComment = async () => {
                const el = document.getElementById(commentId);

                if (el) {
                    //If the comment belongs to a paragraph, scroll the paragraph comments modal,
                    //Otherwise scroll the main window.
                    await animateScrollTo(el, {
                        maxDuration: 200,
                        verticalOffset: -72,
                        cancelOnUserAction: false,
                        elementToScroll: paragraphId ? parentContainer?.target?.parentElement ?? window : window,
                    });

                    setSelectedCommentId(null);

                    navigate(queryString, {
                        preventScrollReset: true,
                        replace: true,
                        state: historyState,
                    });
                }
            };

            if (comments && comments.items.length > 0 && !isPaging && !commentsQuery.isFetching) {
                goToComment();
            }
        }
    }, [
        comments,
        selectedCommentId,
        paragraphId,
        isPaging,
        commentsQuery.isFetching,
        queryString,
        historyState,
        navigate,
        parentContainer,
    ]);

    //#endregion : Submit comment / reply

    //#region : Report comment action handlers
    const [reportComment, setReportComment] = useState<CommentItem | null>(null);
    const [reportOpen, setReportOpen] = useState(false);

    const handleReportOpen = useCallback((comment: CommentItem) => {
        batch(() => {
            setReportComment(comment);
            setReportOpen(true);
        });
    }, []);

    const handleReportClose = useCallback(() => {
        batch(() => {
            setReportComment(null);
            setReportOpen(false);
        });
    }, []);
    //#endregion : Report comment action handlers

    const showPlaceholders = (isPaging && commentsQuery.isFetching) || !comments;

    //#endregion : Variables, functions and api calls

    return (
        <StyledContentSection ref={ref} className={twMerge('!mt-[24px]', className)} {...htmlProps}>
            <div ref={commentsRef} className="space-between flex items-center pb-[12px]">
                <div className="flex-1">
                    <span className="text-[18px] font-bold text-gray-t0">
                        {`${
                            (comments?.pageInfo?.total || 0) > 0
                                ? comments?.pageInfo?.total.toLocaleString('en-US') + ' '
                                : ''
                        } Comments`}
                    </span>
                </div>
                <div>
                    <FormControl variant="outlined">
                        <Select
                            value={sort ? `${sort.value}:${sort.direction}` : ''}
                            onChange={handleSort}
                            fullWidth
                            disabled={!entityId}
                            placeholder="Sort by"
                        >
                            {SortOptions.map(so => (
                                <MenuItem key={so.value} value={so.value}>
                                    {so.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>
            <div className="pb-12">
                <AuthorizationView>
                    <div className="mx-[-20px] mb-[30px] border-b border-[#E8E8E8] px-[20px] pt-[4px] pb-[20px] dark:border-[#313131] sm:mx-0 sm:rounded-[8px] sm:border-none sm:pt-[20px] sm:shadow-[0_1px_5px_0px_rgba(0,0,0,0.15)] sm:dark:bg-white/[.08]">
                        <CommentSubmit
                            onCommentSubmitted={onCommentSubmitted}
                            entityId={entityId}
                            disabled={disabled || !entityId}
                            disabledMessage={disabledMessage}
                            paragraphId={paragraphId}
                        />
                    </div>
                </AuthorizationView>
                {showPlaceholders && (
                    <>
                        {Array(isPaging ? PageCount : 2)
                            .fill(null)
                            .map((_, idx) => (
                                <CommentPlaceholder key={idx} />
                            ))}
                    </>
                )}
                {!showPlaceholders && (
                    <>
                        {comments?.items?.map(comment => (
                            <Comment
                                key={comment.id}
                                entityId={entityId!}
                                comment={comment}
                                onReplySubmitted={onReplySubmitted}
                                onReportComment={handleReportOpen}
                                paragraphId={paragraphId}
                                replyDisabled={disabled}
                                replyDisabledMessage={disabledMessage}
                            />
                        ))}
                        {(comments?.totalTopLevelItems || 0) === 0 && (
                            <AuthorizationView
                                unauthorizedComponent={<div>Sign in and be the first to leave a comment!</div>}
                            >
                                <div>Be the first to leave a comment!</div>
                            </AuthorizationView>
                        )}
                    </>
                )}
            </div>
            <CommentReport
                reportComment={reportComment}
                reportOpen={reportOpen}
                onReportCancelled={handleReportClose}
                onReportSubmitted={handleReportClose}
            />
            {(comments?.totalTopLevelItems || 0) > 0 && (
                <div className="py-8">
                    <Paging count={totalPages} page={page} onChange={handlePagination} />
                </div>
            )}
        </StyledContentSection>
    );
}

//#endregion : Main component

export default React.memo(CommentsAreaComponent);
